import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { useFirebase } from '../contexts/FirebaseContext'
import { 
  ArrowLeft, 
  BookOpen,
  FileText,
  Calendar,
  Edit,
  Target
} from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'

const NursingBulletView = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  
  const [bullet, setBullet] = useState(null)
  const [loading, setLoading] = useState(true)
  
  const { getNursingBullets } = useFirebase()

  useEffect(() => {
    loadBullet()
  }, [id])

  const loadBullet = async () => {
    try {
      setLoading(true)
      const bullets = await getNursingBullets()
      const foundBullet = bullets.find(b => b.id === id)
      if (foundBullet) {
        setBullet(foundBullet)
      } else {
        toast.error('Nursing bullet not found')
        navigate('/nursing-bullets')
      }
    } catch (error) {
      console.error('Error loading bullet:', error)
      toast.error('Failed to load nursing bullet')
      navigate('/nursing-bullets')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  if (!bullet) {
    return (
      <div className="text-center py-12">
        <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Nursing Bullet Not Found</h3>
        <p className="text-gray-600 mb-4">The requested nursing bullet could not be found.</p>
        <Link to="/nursing-bullets" className="btn-primary">
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to Nursing Bullets
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl shadow-xl p-8 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/nursing-bullets')}
              className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
            <div>
              <h1 className="text-3xl font-bold mb-2 flex items-center">
                <BookOpen className="h-8 w-8 mr-3" />
                Nursing Bullet Details
              </h1>
              <p className="text-blue-100">
                Complete information and bullet points overview
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Link
              to={`/nursing-bullets/add-points/${bullet.id}`}
              className="btn-white"
            >
              <Edit className="h-5 w-5 mr-2" />
              Edit Bullet Points
            </Link>
          </div>
        </div>
      </div>

     {/* Main Details Card */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <BookOpen className="h-6 w-6 mr-3 text-blue-600" />
            {bullet.title}
          </h2>
        </div>
        
        <div className="card-body space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Title</label>
                <div className="flex items-start text-gray-900">
                  <Target className="h-4 w-4 mr-2 text-gray-400 mt-1" />
                  <p className="font-medium">{bullet.title}</p>
                </div>
              </div>

              {bullet.description && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Description</label>
                  <p className="text-gray-900 leading-relaxed pl-6">{bullet.description}</p>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Total Bullet Points</label>
                <div className="flex items-center text-gray-900">
                  <FileText className="h-4 w-4 mr-2 text-blue-500" />
                  <span className="text-2xl font-bold text-blue-600">{bullet.bulletPoints?.length || 0}</span>
                  <span className="text-sm text-gray-500 ml-2">points</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Created Date</label>
                <div className="flex items-center text-gray-900">
                  <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                  <span>
                    {bullet.createdAt ? 
                      new Date(bullet.createdAt).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: '2-digit', 
                        year: 'numeric'
                      }) 
                      : 'N/A'
                    }
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Content Section */}
          {bullet.content && (
            <div className="pt-6 border-t border-gray-200">
              <label className="block text-sm font-medium text-gray-500 mb-3">Content</label>
              <div className="prose max-w-none bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-900 leading-relaxed whitespace-pre-wrap">{bullet.content}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default NursingBulletView